import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, X, ChevronDown, ChevronUp, Star } from 'lucide-react';
import './FeaturePage.css'; // Import the styles

const FeaturePage = ({ 
  hero, 
  socialProof, 
  benefits, 
  howItWorks, 
  comparison, 
  faq, 
  cta 
}) => {
  
  // FAQ Accordion State
  const [openFaq, setOpenFaq] = useState(null);
  const toggleFaq = (index) => setOpenFaq(openFaq === index ? null : index);

  return (
    <div className="feature-page-wrapper">
      
      {/* 1. HERO SECTION */}
      <section className="hero-section">
        <div className="container hero-grid">
          <div className="hero-content">
            <h1 className="hero-title">{hero.title}</h1>
            <p className="hero-sub">{hero.subtitle}</p>
            <div className="hero-actions">
              <Link to={hero.primaryLink} className="btn btn-primary">{hero.primaryBtn}</Link>
              <Link to={hero.secondaryLink} className="btn btn-secondary">{hero.secondaryBtn}</Link>
            </div>
            {hero.trustBadge && (
              <div className="hero-trust">
                <div className="trust-stars"><Star size={14} fill="#fbbf24" stroke="none" /> 5.0</div>
                <span>{hero.trustBadge}</span>
              </div>
            )}
          </div>
          <div className="hero-visual">
            <div className="hero-img-wrapper">
              <img src={hero.image} alt="Apparel Mockup Demo" className="hero-img" />
              <div className="hero-glow" />
            </div>
          </div>
        </div>
      </section>

      {/* 2. SOCIAL PROOF */}
      {socialProof && (
        <section className="social-proof-section">
          <div className="container">
            {/* The Stats Row */}
            <div className="stat-grid">
              {socialProof.stats.map((stat, idx) => (
                <div key={idx} className="stat-item">
                  <span className="stat-val">{stat.value}</span>
                  <span className="stat-label">{stat.label}</span>
                </div>
              ))}
            </div>

            {/* The Brands Row */}
            <div className="brand-section">
              {socialProof.title && <p className="brand-title">{socialProof.title}</p>}
              <div className="brand-grid">
                {socialProof.brands.map((brand, idx) => (
                  <span key={idx} className="brand-text">{brand}</span>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 3. BENEFITS GRID */}
      <section className="benefits-section">
        <div className="container">
          <div className="section-header">
            <h2>Why Choose FullMockup?</h2>
          </div>
          <div className="benefits-grid">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="benefit-card">
                <div className="benefit-icon">{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. HOW IT WORKS */}
      {howItWorks && (
        <section className="how-section">
          <div className="container">
            <div className="section-header">
              <h2>How It Works</h2>
              <p>Professional results in 3 simple steps</p>
            </div>
            <div className="steps-grid">
              {howItWorks.map((step, idx) => (
                <div key={idx} className="step-card">
                  <div className="step-number">0{idx + 1}</div>
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 5. COMPARISON TABLE (Structure remains, styling does the work) */}
      {comparison && (
        <section className="comparison-section">
          <div className="container">
            <div className="section-header">
              <h2>{comparison.title}</h2>
            </div>
            <div className="comparison-table-wrapper">
              <table className="comparison-table">
                <thead>
                  <tr>
                    <th className="th-feature">Feature</th>
                    <th className="th-us">
                        <div className="th-badge">FullMockup (Us)</div>
                    </th>
                    <th className="th-them">Competitors</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.rows.map((row, idx) => (
                    <tr key={idx}>
                      <td className="td-feature">{row.feature}</td>
                      <td className="td-us">
                        {/* Added Check Icon automatically for 'Us' column */}
                        <Check size={18} className="icon-check" strokeWidth={3} /> 
                        <span className="td-text">{row.us}</span>
                      </td>
                      <td className="td-them">
                        {/* Logic to show X if text contains "No" or negative words */}
                        {(row.them.includes('No') || row.them.includes('High') || row.them.includes('Expensive')) 
                            ? <X size={18} className="icon-x" /> 
                            : null} 
                        {row.them}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* 6. FAQ SECTION */}
      {faq && (
        <section className="faq-section">
          <div className="container">
            <div className="section-header">
              <h2>Frequently Asked Questions</h2>
            </div>
            
            {/* Using a Wrapper instead of Grid for Line-by-Line */}
            <div className="faq-wrapper">
              {faq.map((item, idx) => (
                <div key={idx} className="faq-card">
                  {/* Added Q Icon for visual interest */}
                  <div className="faq-icon-box">?</div>
                  
                  <div className="faq-content">
                    <h4>{item.q}</h4>
                    <p>{item.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 7. FINAL CTA */}
      <section className="final-cta-section">
        <div className="container cta-container">
          <h2>{cta.title}</h2>
          <p>{cta.subtitle}</p>
          <Link to={cta.link} className="btn btn-primary btn-large">{cta.btnText}</Link>
          <small>{cta.subText}</small>
        </div>
      </section>

    </div>
  );
};

export default FeaturePage;